import { sourcePagePath } from './attribution'

type EventProps = Record<string, string | number | boolean>

type UmamiPayload = Record<string, unknown>

interface Umami {
  track(payload?: string | ((props: UmamiPayload) => UmamiPayload), data?: EventProps): void
}

declare global {
  interface Window {
    umami?: Umami
  }
}

// Met data-auto-track="false" installeert de Umami-tracker zijn pushState-hook
// niet en blijft zijn interne URL op de landingspagina staan. Daarom geven we
// bij elk event de actuele URL expliciet mee — inclusief query string, zodat
// utm_* parameters van de landing in het UTM-rapport terechtkomen.
//
// Persoonsgegevens en geheimen horen niet in analytics: het e-mailadres dat de
// boekingsflow aan /register en /login meegeeft en het hersteltoken uit de
// herstelmail (/?lead=…) worden uit de gerapporteerde URL gehaald. Hetzelfde
// geldt voor het galerijtoken van de booth (/g/…), ook als het in de
// ?redirect= van de loginpagina zit.
const PRIVATE_QUERY_PARAMS = ['email', 'lead']

export function isPrivatePath(path: string): boolean {
  return path === '/g' || path.startsWith('/g/')
}

export function analyticsUrl(href: string = window.location.href): string {
  try {
    const url = new URL(href)
    for (const key of PRIVATE_QUERY_PARAMS) url.searchParams.delete(key)
    const redirect = url.searchParams.get('redirect')
    if (redirect && isPrivatePath(redirect)) url.searchParams.delete('redirect')
    if (isPrivatePath(url.pathname)) url.pathname = '/g'
    return url.toString()
  } catch {
    return href
  }
}

function withCurrentUrl(extra?: UmamiPayload): (props: UmamiPayload) => UmamiPayload {
  return props => ({ ...props, url: analyticsUrl(), title: document.title, ...extra })
}

// Events van de boekingsfunnel krijgen de klikpagina op boenkop.be (src, het
// pad van de pagina waar op de portaallink geklikt werd) als property, zodat
// de funnel in Umami per pagina van de site te filteren is.
const SOURCE_PAGE_EVENTS = new Set(['Booking Step'])

export function eventProps(name: string, props?: EventProps): EventProps | undefined {
  if (!SOURCE_PAGE_EVENTS.has(name) || props?.src !== undefined) return props
  const src = sourcePagePath()
  return src ? { ...props, src } : props
}

export function trackEvent(name: string, props?: EventProps): void {
  if (typeof window === 'undefined' || typeof window.umami?.track !== 'function') return
  window.umami.track(withCurrentUrl({ name, data: eventProps(name, props) }))
}

export function trackPageview(): void {
  if (typeof window === 'undefined' || typeof window.umami?.track !== 'function') return
  window.umami.track(withCurrentUrl())
}

// Engagement tracking — scroll depth + active time, reset per route in SPA
type EngagementCleanup = () => void
let cleanupCurrent: EngagementCleanup | null = null

export function startEngagementTracking(pagePath: string): void {
  if (typeof window === 'undefined') return
  // Tear down prior route's listeners before starting a new one
  if (cleanupCurrent) cleanupCurrent()

  const scrollFired = new Set<number>()
  const scrollMilestones = [25, 50, 75, 100]
  let raf = 0
  const onScroll = () => {
    if (raf) return
    raf = requestAnimationFrame(() => {
      raf = 0
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable < 200) return
      const pct = Math.min(100, Math.round((window.pageYOffset / scrollable) * 100))
      for (const m of scrollMilestones) {
        if (pct >= m && !scrollFired.has(m)) {
          scrollFired.add(m)
          trackEvent('Scroll', { depth: `${m}%`, page: pagePath })
        }
      }
    })
  }
  window.addEventListener('scroll', onScroll, { passive: true })

  const timeFired = new Set<number>()
  const timeMilestones = [30, 60, 120]
  let active = !document.hidden
  let elapsed = 0
  let lastTick = Date.now()
  const tick = () => {
    const now = Date.now()
    if (active) {
      elapsed += (now - lastTick) / 1000
      for (const m of timeMilestones) {
        if (elapsed >= m && !timeFired.has(m)) {
          timeFired.add(m)
          trackEvent('Engaged Time', { seconds: `${m}s`, page: pagePath })
        }
      }
    }
    lastTick = now
  }
  const intervalId = window.setInterval(tick, 1000)
  const onVisibility = () => {
    tick()
    active = !document.hidden
    lastTick = Date.now()
  }
  document.addEventListener('visibilitychange', onVisibility)

  cleanupCurrent = () => {
    window.removeEventListener('scroll', onScroll)
    document.removeEventListener('visibilitychange', onVisibility)
    window.clearInterval(intervalId)
  }
}
